import 'uplot/dist/uPlot.min.css'

import { plural, t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { InterpolatedValueTooltip } from 'features/timeSeries/components/InterpolatedValueTooltip'
import { NoDataInRange } from 'features/timeSeries/components/timeSeriesScatterplot/NoDataInRange'
import { TimeSeriesLoadingAnimation } from 'features/timeSeries/components/timeSeriesScatterplot/TimeSeriesLoadingAnimation'
import type { UPlotSeriesAppearance } from 'features/timeSeries/components/timeSeriesScatterplot/useUPlotGenerators'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import { useUPlotChart } from 'features/timeSeries/hooks/useUPlotChart'
import { perfCount } from 'features/timeSeries/perf/processDataPerf'
import {
  ScatterPlotEmptyStateContainer,
  ScatterPlotEmptyStateDescription,
  ScatterPlotEmptyStateTitle,
} from 'features/timeSeries/sharedStyles'
import React, { MutableRefObject, useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { SensorScatterplotData } from 'types'
import uPlot from 'uplot'

const ChartContainer = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;

  .uplot {
    font-family: 'Manrope', sans-serif;
  }

  .u-title {
    color: var(--muted-foreground);
  }

  .u-legend {
    display: none;
  }

  .u-select {
    background: ${CHART_COLORS.SELECTION_BG} !important;
    border: none !important;
    box-shadow: inset 0 0 0 2px ${CHART_COLORS.CURSOR_ACCENT};
  }

  .u-cursor-x,
  .u-cursor-y {
    border-color: ${CHART_COLORS.CURSOR_ACCENT_LIGHT} !important;
  }

  .u-wrap {
    background: ${CHART_COLORS.BACKGROUND};
  }
`

const ChartArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  background: ${CHART_COLORS.BACKGROUND};
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`

const EmptyStateContainer = styled(ScatterPlotEmptyStateContainer)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`

const LoadingStateContainer = styled(EmptyStateContainer).attrs<{
  'data-testid'?: string
}>(() => ({
  role: 'status',
  'aria-live': 'polite',
  'aria-label': t`Loading`,
  'data-testid': 'time-series-centered-loader',
}))`
  gap: 0.875rem;
  min-width: 16rem;
  padding: 1.25rem 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
  box-shadow: none;
`

const LoadingStateTitle = styled(ScatterPlotEmptyStateTitle)`
  margin: 0;
`

const LoadingStateDescription = styled(ScatterPlotEmptyStateDescription)`
  margin: 0;
  line-height: 1.4;
  max-width: 14rem;
`

interface TimeSeriesScatterplotChartProps {
  handleMouseMove: (event: React.MouseEvent<HTMLDivElement>) => void
  handleMouseLeave: () => void
  internalRef: React.MutableRefObject<HTMLDivElement | null>
  cursorPosition: {
    x: number
    y: number
    clientX: number
    clientY: number
  } | null
  tableData: SensorScatterplotData[]
  sensorVisibilityConfig: Record<string, boolean>
  shouldShowSpinner: boolean
  shouldAnimateSpinner: boolean
  hasDataPoints: boolean
  uPlotOptions: uPlot.Options
  uPlotRecreateKey: string
  uPlotSeriesAppearance: UPlotSeriesAppearance[]
  uPlotData: uPlot.AlignedData
  uPlotRef: MutableRefObject<uPlot | null>
  isLoading: boolean
  showEmptyState: boolean
  selectedSensors: Array<{ id: string }>
  derivedValues: Array<{ id: string }>
  hideTooltip?: boolean
}

export const TimeSeriesScatterplotChart = React.forwardRef<
  HTMLDivElement,
  TimeSeriesScatterplotChartProps
>(
  (
    {
      handleMouseMove,
      handleMouseLeave,
      internalRef,
      cursorPosition,
      tableData,
      sensorVisibilityConfig,
      hasDataPoints,
      uPlotOptions,
      uPlotRecreateKey,
      uPlotSeriesAppearance,
      uPlotData,
      uPlotRef,
      isLoading,
      showEmptyState,
      selectedSensors,
      derivedValues,
      hideTooltip,
    },
    ref
  ) => {
    const { chartContainerRef, chartAreaRef } = useUPlotChart({
      uPlotOptions,
      uPlotRecreateKey,
      uPlotSeriesAppearance,
      uPlotData,
      uPlotRef,
      hasDataPoints,
    })

    const setChartAreaRef = useCallback(
      (node: HTMLDivElement | null) => {
        chartAreaRef.current = node
        internalRef.current = node
        if (ref) {
          if (typeof ref === 'function') {
            ref(node)
          } else {
            ;(ref as MutableRefObject<HTMLDivElement | null>).current = node
          }
        }
      },
      [chartAreaRef, internalRef, ref]
    )

    const shouldShowCenteredLoadingState =
      !hasDataPoints && (isLoading || !showEmptyState)
    const shouldShowEmptyState =
      !hasDataPoints && !shouldShowCenteredLoadingState

    // Counted on the rising edge only: each appearance of the full-screen loader
    // is one instance of the user being thrown out of the chart.
    const wasShowingCenteredLoaderRef = useRef(false)
    useEffect(() => {
      if (
        shouldShowCenteredLoadingState &&
        !wasShowingCenteredLoaderRef.current
      ) {
        perfCount('centeredLoaderShows')
      }
      wasShowingCenteredLoaderRef.current = shouldShowCenteredLoadingState
    }, [shouldShowCenteredLoadingState])

    const wasShowingEmptyStateRef = useRef(false)
    useEffect(() => {
      if (shouldShowEmptyState && !wasShowingEmptyStateRef.current) {
        perfCount('emptyStateShows')
      }
      wasShowingEmptyStateRef.current = shouldShowEmptyState
    }, [shouldShowEmptyState])

    return (
      <ChartArea
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        ref={setChartAreaRef}>
        {!hideTooltip && (
          <InterpolatedValueTooltip
            cursorPosition={cursorPosition}
            tableData={tableData}
            sensorVisibilityConfig={sensorVisibilityConfig}
          />
        )}
        <ChartContainer ref={chartContainerRef} />
        {!hasDataPoints &&
          (() => {
            const hasConfiguredData =
              selectedSensors.length > 0 || derivedValues.length > 0

            if (shouldShowCenteredLoadingState) {
              const penCount = selectedSensors.length + derivedValues.length
              return (
                <LoadingStateContainer>
                  <TimeSeriesLoadingAnimation />
                  <LoadingStateTitle>
                    <Trans>Loading</Trans>
                  </LoadingStateTitle>
                  <LoadingStateDescription>
                    {penCount >= 8
                      ? plural(penCount, {
                          one: 'Fetching # pen — this can take a moment.',
                          other: 'Fetching # pens — this can take a moment.',
                        })
                      : penCount > 0
                        ? plural(penCount, {
                            one: 'Fetching # pen.',
                            other: 'Fetching # pens.',
                          })
                        : t`Preparing chart data for the plot.`}
                  </LoadingStateDescription>
                </LoadingStateContainer>
              )
            } else if (hasConfiguredData) {
              return (
                <EmptyStateContainer>
                  <NoDataInRange />
                </EmptyStateContainer>
              )
            } else {
              return (
                <EmptyStateContainer>
                  <ScatterPlotEmptyStateTitle>
                    <Trans>No data</Trans>
                  </ScatterPlotEmptyStateTitle>
                  <ScatterPlotEmptyStateDescription>
                    <Trans>
                      Use the Edit pens button to add data to the plot.
                    </Trans>
                  </ScatterPlotEmptyStateDescription>
                </EmptyStateContainer>
              )
            }
          })()}
      </ChartArea>
    )
  }
)

TimeSeriesScatterplotChart.displayName = 'TimeSeriesScatterplotChart'
